// Safe-area computation for the Three.js stage.
//
// When the streamer enables a camera corner, we reserve a 480x270 region (16:9
// camera composite). The leaderboard becomes a vertical column hugging that
// same edge. Gameplay scenes lay out within the L-shape that remains.
//
// Coordinates are in stage-space (origin at center, +y up, in pixels). The
// SafeArea is returned as a top-left-origin rect for convenience: x/y are the
// upper-left corner of the gameplay region in stage-space, width/height extend
// down/right from there.

import type { CameraPosition } from '@sg/shared';

export const STAGE_W = 1920;
export const STAGE_H = 1080;
export const CAM_W = 480;
export const CAM_H = 270;

export interface SafeArea {
  /** Upper-left corner of the gameplay region (stage-space, +y up, origin = center). */
  x: number;
  y: number;
  /** Dimensions of the gameplay region. */
  width: number;
  height: number;
  /** Camera placement; 'none' means no reservation. */
  cameraPosition: CameraPosition;
}

export interface CameraRect {
  /** Upper-left corner of the camera area in stage-space. */
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The leaderboard column when a corner is selected. */
export interface LeaderboardRect {
  /** Upper-left corner in stage-space. */
  x: number;
  y: number;
  width: number;
  height: number;
}

const HALF_W = STAGE_W / 2;
const HALF_H = STAGE_H / 2;

export function getCameraRect(pos: CameraPosition): CameraRect | null {
  if (pos === 'none') return null;
  const right = pos === 'top-right' || pos === 'bottom-right';
  const top = pos === 'top-left' || pos === 'top-right';
  const x = right ? HALF_W - CAM_W : -HALF_W;
  const y = top ? HALF_H : -HALF_H + CAM_H;
  return { x, y, width: CAM_W, height: CAM_H };
}

export function getLeaderboardRect(pos: CameraPosition): LeaderboardRect | null {
  if (pos === 'none') return null;
  const right = pos === 'top-right' || pos === 'bottom-right';
  const top = pos === 'top-left' || pos === 'top-right';
  const x = right ? HALF_W - CAM_W : -HALF_W;
  // If camera is at the top, leaderboard sits below it (and reaches the bottom edge);
  // if camera is at the bottom, leaderboard sits above it (top edge down to camera).
  if (top) {
    // Below the camera, occupying the same column down to the bottom of the stage.
    const y = HALF_H - CAM_H;
    return { x, y, width: CAM_W, height: STAGE_H - CAM_H };
  } else {
    // Above the bottom-corner camera, from the top of the stage to the camera top.
    const y = HALF_H;
    return { x, y, width: CAM_W, height: STAGE_H - CAM_H };
  }
}

/** Compute the gameplay safe-area for a given camera position. */
export function computeSafeArea(pos: CameraPosition): SafeArea {
  if (pos === 'none') {
    return {
      x: -HALF_W,
      y: HALF_H,
      width: STAGE_W,
      height: STAGE_H,
      cameraPosition: pos,
    };
  }
  // The camera + leaderboard occupy the same full-height vertical column on one
  // side. Gameplay is the remaining (STAGE_W - CAM_W) wide column on the other side.
  const right = pos === 'top-right' || pos === 'bottom-right';
  const x = right ? -HALF_W : -HALF_W + CAM_W;
  return {
    x,
    y: HALF_H,
    width: STAGE_W - CAM_W,
    height: STAGE_H,
    cameraPosition: pos,
  };
}

/** Center point of the safe area (handy for scene origin placement). */
export function centerOf(area: SafeArea): { x: number; y: number } {
  return {
    x: area.x + area.width / 2,
    y: area.y - area.height / 2,
  };
}

/** Parse a `?cam=<pos>` URL override; returns null if absent or invalid. */
export function parseCameraOverride(search: string): CameraPosition | null {
  const params = new URLSearchParams(search);
  const v = params.get('cam');
  if (!v) return null;
  if (v === 'none' || v === 'top-left' || v === 'top-right' || v === 'bottom-left' || v === 'bottom-right')
    return v;
  return null;
}
